/**
 * Module registry — tracks installed modules that are active in App mode.
 *
 * Modules are registered dynamically after the Marketplace API confirms they
 * are installed. Nothing is pre-registered here at import time; the
 * loadModulesFromAPI() function in moduleLoader.ts populates the registry.
 */

import { ComponentType } from 'react';
import { logger } from '@/utils/logger';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface ModuleMenuItem {
  label: string;
  path: string;
}

export interface InstalledModule {
  id: string;
  name: string;
  version: string;
  /** Base URL path, e.g. "/my-module/finance" */
  path: string;
  /** Root component — should contain its own nested <Routes> */
  component: ComponentType;
  /** Sidebar navigation items provided by the module */
  menuItems?: ModuleMenuItem[];
}

// ────────────────────────────────────────────────────────────────────────────
// In-memory registry
// ────────────────────────────────────────────────────────────────────────────

const moduleRegistry = new Map<string, InstalledModule>();

export function registerModule(module: InstalledModule): void {
  moduleRegistry.set(module.id, module);
  logger.log(`Module registered: ${module.name} (${module.id})`);
}

export function getModule(moduleId: string): InstalledModule | undefined {
  return moduleRegistry.get(moduleId);
}

export function getAllModules(): InstalledModule[] {
  return Array.from(moduleRegistry.values());
}

export function isModuleInstalled(moduleId: string): boolean {
  return moduleRegistry.has(moduleId);
}

export function unregisterModule(moduleId: string): void {
  moduleRegistry.delete(moduleId);
  logger.log(`Module unregistered: ${moduleId}`);
}

/**
 * Returns only the modules whose id is present in the provided list.
 * Pass `connection.enabled_modules` to get the modules active for a connection.
 * If enabledIds is null/undefined (no connection selected), returns empty array.
 */
export function getEnabledModules(enabledIds: string[] | null | undefined): InstalledModule[] {
  if (!enabledIds) return [];
  const normalised = new Set(
    enabledIds.map((id) => id.toLowerCase().replace(/[\s-]+/g, '_')),
  );
  return Array.from(moduleRegistry.values()).filter((m) => normalised.has(m.id));
}

/* Local frontend catalogue: `@/modules/localFrontends` (modules zone, not core). */
